import React, { type ReactNode } from 'react';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
} from '@rocco/components/carousel';

import type {
    CarouselOptions,
    OnChangeSlideEvent,
} from '@rocco/components/carousel';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-chevron-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-chevron-right.svg';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { cn } from '@rocco/utils/cn';

const _carouselItemVariant = cva(
    'mr-[1rem] max-w-[20.25rem] first:ml-0 last:mr-0 md:mr-[1.5rem] md:max-w-[25.125rem]',
    {
        variants: {
            size: {
                default: 'basis-full',
                medium: 'md:max-w-[27.15rem]',
                recentlyViewed: 'md:max-w-[21.45rem]',
                // The articles strip: the small phone card, the desktop
                // recently-viewed measure.
                editorialStrip: 'max-w-[10.75rem] md:max-w-[21.45rem]',
                suggestedCategories: 'md:max-w-[17rem]',
                mobileSearchResults: 'mr-3.25 max-w-54.75',
                small: 'max-w-[10.75rem]',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    },
);

interface CarouselSectionProps
    extends React.PropsWithChildren, VariantProps<typeof _carouselItemVariant> {
    title?: ReactNode;
    titleClassName?: string;
    /** Merged onto the title/arrows row — e.g. to override its gutter. */
    headerClassName?: string;
    /**
     * Merged onto the scroller viewport (where the `fullPage` gutter lives),
     * so header and slides can be re-guttered together.
     */
    contentClassName?: string;
    showArrows?: boolean;
    onChangeSlide?: (event: OnChangeSlideEvent) => void;
    dragFree?: boolean;
}

export const CarouselSection = ({
    title,
    titleClassName,
    headerClassName,
    contentClassName,
    children,
    size,
    showArrows = true,
    onChangeSlide,
    dragFree: dragFreeProp,
}: CarouselSectionProps) => {
    const carouselOptions: CarouselOptions = {
        slidesToScroll: 1,
        ...(dragFreeProp && { align: 'start' as const, dragFree: true }),
        breakpoints: {
            '(min-width: 768px)': {
                slidesToScroll: 4,
            },
        },
        watchDrag: (api, evt) => {
            // Only allow dragging on the main carousel element
            // Prevent dragging when interacting with child carousels
            return (
                evt.target instanceof Element &&
                !evt.target.closest('.slider-item-image')
            );
        },
    };

    return (
        <Carousel options={carouselOptions} onChangeSlide={onChangeSlide}>
            {title || showArrows ? (
                <div
                    className={cn(
                        'md:mb-spacing-page-category-gap-component-s relative mb-6 flex items-center justify-between px-6 md:px-12 md:py-[0.75rem]',
                        headerClassName,
                    )}
                >
                    {title && (
                        <h2
                            className={
                                titleClassName
                                    ? titleClassName
                                    : 'text-6 font-medium tracking-[0.025rem] md:left-12'
                            }
                        >
                            {title}
                        </h2>
                    )}

                    <div className="flex gap-2 md:right-12 md:min-h-[2.5rem]">
                        {showArrows && (
                            <>
                                <CarouselPrevious>
                                    <ArrowLeft className="size-[1.125rem]" />
                                </CarouselPrevious>
                                <CarouselNext>
                                    <ArrowRight className="size-[1.125rem]" />
                                </CarouselNext>
                            </>
                        )}
                    </div>
                </div>
            ) : null}
            <CarouselContent
                variant="fullPage"
                viewportClassName={contentClassName}
            >
                {React.Children.map(children, (child) => (
                    <CarouselItem className={_carouselItemVariant({ size })}>
                        {child}
                    </CarouselItem>
                ))}
            </CarouselContent>
        </Carousel>
    );
};
